import React from 'react';
import config from 'src/config/config';
import {
  COLLEGE_FUND_NOTE_PHONE_NUMBERS,
  PRODUCT_TYPE_LABEL_MAP,
  TOLL_FREE_NUMBER,
} from 'src/settings/settings';
import {
  calculateAndFormatUnixDates,
  futureDate,
} from 'src/utils/dates/date-utility';
import { isCollegeDistribute, isSell } from 'src/utils/transactions-util';
import words from 'src/utils/words';
import If from '../../Shared/If/If';
import Phone from '../../Shared/Phone/Phone';
import TRP from '../../Shared/TRP/TRP';
import SharedZeroState from './SharedZeroState';
import './ZeroState.scss';
import ZeroStateWithBankStatus from './ZeroStateWithBankStatus';

const ZeroState = ({
  customerWebViewEnabled,
  isSalesforceRep,
  collegePlanType,
  collegeTypeCount,
  numberOfEligibleBanks,
  effectiveAddressChangedFlag,
  eligibleBanks,
  isBanksWithStatus,
  stopMail,
  stopTransfer,
  zeroStateEffectiveDate,
  isAddressChangedOnSameDay,
}) => {
  const date = calculateAndFormatUnixDates(
    futureDate(zeroStateEffectiveDate, 15),
    'MM/DD/YY'
  );

  const hasBanks = numberOfEligibleBanks > 0;
  const hasChangedAddress =
    isAddressChangedOnSameDay || effectiveAddressChangedFlag;
  const phoneNumber = isCollegeDistribute()
    ? COLLEGE_FUND_NOTE_PHONE_NUMBERS[collegePlanType] || TOLL_FREE_NUMBER
    : TOLL_FREE_NUMBER;
  const returnTo =
    isCollegeDistribute() && collegeTypeCount === 1 ? (
      PRODUCT_TYPE_LABEL_MAP[collegePlanType]
    ) : (
      <TRP />
    );

  return (
    <div className="zero-state-container">
      <If test={stopMail && hasChangedAddress && hasBanks}>
        <SharedZeroState
          className={'e2e-zero-state-return-mail'}
          descriptionLine1={
            <div className="zero-state-address-undeliverable">
              This change will be effective as of the following business day. If
              you have a valid and associated bank on file, please check back
              tomorrow to place a ACH or Wire {words.redemption}. If you will be
              requesting your {words.redemption} by check, there will be a 15
              day hold for receiving checks after the mailing address has been
              changed. Please call us at <Phone phoneNumber={phoneNumber} /> to
              discuss the options available to you for placing a{' '}
              {words.redemption}.
            </div>
          }
          heading={'You have recently updated your mailing address.'}
        />
      </If>
      <If test={stopMail && !effectiveAddressChangedFlag}>
        <SharedZeroState
          className={'e2e-zero-state-stop-mail'}
          descriptionLine1={
            <div>
              Mail sent to your address of record was marked undeliverable and
              returned to {returnTo} by the Post Office.
            </div>
          }
          heading={'Address Marked as Undeliverable'}
          link={
            <div className="zero-state-address-undeliverable">
              <a
                className="college-branded-link zero-state-link"
                href={`${config.url}/usis/my-accounts/profile/contact-information`}
              >
                Update your address of record.
              </a>{' '}
              It may take up to 24 hours for changes to go through, and{' '}
              {isSell() ? (
                <span>
                  during the 15 days following a change to your mailing address
                  of record, proceeds from a redemption submitted online can be
                  sent only to a previously linked bank account and cannot be
                  sent by check. If your address is correct or to discuss other
                  redemption options, please{' '}
                  <a
                    href={`${config.url}/personal-investing/help/contact-us.html`}
                    rel="noreferrer"
                    target="_blank"
                  >
                    contact us
                  </a>
                  .
                </span>
              ) : (
                <span>
                  will result in a 15 day hold on {words.redeeming} via check.
                  {!(!customerWebViewEnabled && isSalesforceRep) && (
                    <div>
                      To discuss other {words.redemption} options, call{' '}
                      <Phone phoneNumber={phoneNumber} />.
                    </div>
                  )}
                </span>
              )}
            </div>
          }
        />
      </If>
      <If test={stopTransfer && !stopMail && !isCollegeDistribute()}>
        <SharedZeroState
          className={'e2e-zero-state-stop-transfer'}
          descriptionLine1={
            <div>
              <a
                href={`${config.url}/personal-investing/help/contact-us.html`}
                rel="noreferrer"
                target="_blank"
              >
                Contact us
              </a>{' '}
              to speak with an associate.
            </div>
          }
          heading={
            <div>
              {' '}
              <TRP /> has blocked transactions on this {words.fund} or account.
            </div>
          }
        />
      </If>
      <If test={stopTransfer && !stopMail && isCollegeDistribute()}>
        <SharedZeroState
          className={'e2e-zero-state-stop-transfer'}
          descriptionLine1={
            <span>
              {!(!customerWebViewEnabled && isSalesforceRep) && (
                <div>
                  Call <Phone phoneNumber={phoneNumber} /> to speak with an
                  associate.
                </div>
              )}
            </span>
          }
          heading={
            <div> Transactions are blocked on this portfolio or account.</div>
          }
        />
      </If>
      <If test={!stopTransfer && effectiveAddressChangedFlag}>
        <If test={isBanksWithStatus}>
          <ZeroStateWithBankStatus
            className={'e2e-zero-state-non-verified-bank'}
            date={date}
            eligibleBanks={eligibleBanks}
          />
        </If>
        <If test={!hasBanks}>
          <SharedZeroState
            className={'e2e-zero-state-no-bank'}
            descriptionLine1={
              <div>
                <span className="zero-state-display">
                  {' '}
                  Your mailing address was recently updated, but you have no
                  active banks on file. During the 15 days following a change to
                  your mailing address of record, proceeds from a{' '}
                  {`${words.redemption}`} submitted online can be sent only to a
                  previously linked bank account and cannot be sent by check.
                  Please{' '}
                  <a
                    href={`${config.url}/personal-investing/help/contact-us.html`}
                    rel="noreferrer"
                    target="_blank"
                  >
                    contact us
                  </a>{' '}
                  to discuss the options available to you for placing a{' '}
                  {`${words.redemption}`}.
                </span>
              </div>
            }
            heading={'Address Change in Progress'}
            link={
              <div>
                <span className="zero-state-date">
                  This option will be available online on {date}.
                </span>
              </div>
            }
          />
        </If>
      </If>
    </div>
  );
};

export default ZeroState;
